/**
 * Pages asked to do something as they open (from the omnibar: "New item" opens the items page with its dialog open),
 * and settings asked to be shown (a link's `#anchor`: the page scrolls to it, and its control takes the focus).
 */
import { useEffect } from "preact/hooks";
import { location, setQuery } from "./router.ts";

/** The query parameter saying what a page is asked to do. */
const ASK = "do";
/** What the items page is asked: its "New item" dialog. */
export const NEW_ITEM = "new";
/** …the same dialog, making a document. */
export const NEW_DOCUMENT = "new-doc";
/** …or a spreadsheet. */
export const NEW_SPREADSHEET = "new-sheet";
/** What the members page is asked: its "Invite a person" dialog. */
export const INVITE = "invite";
/** How long a page may take to show the setting a link's `#anchor` names (its data loading). */
const WAIT_FRAMES = 120;

/** `href`, asking its page to do `what` as it opens. */
export const askHref = (href: string, what: string): string =>
  `${href}${href.includes("?") ? "&" : "?"}${ASK}=${encodeURIComponent(what)}`;

/** When the page was asked to do `what`: `then`, once (the request then leaves the address). */
export function useAsked(what: string, then: () => void): void {
  const asked = location.value.searchParams.get(ASK) === what;
  // biome-ignore lint/correctness/useExhaustiveDependencies: `then` is what to do, not when.
  useEffect(() => {
    if (asked) {
      setQuery({ [ASK]: null });
      then();
    }
  }, [asked]);
}

/** What takes the focus in a setting: its first control. */
const CONTROL = "input:not([type=hidden]), select, textarea, button";

/**
 * Shows what the address's `#anchor` names, once it's there (a page may load first): scrolled to the middle, its first
 * control focused.
 */
export function useAnchor(): void {
  const hash = location.value.hash;
  useEffect(() => {
    let id = "";
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch {
      // A malformed anchor names nothing.
    }
    if (!id) {
      return;
    }
    let frames = 0;
    let frame = 0;
    const look = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView?.({ block: "center" });
        el.querySelector<HTMLElement>(CONTROL)?.focus({ preventScroll: true });
      } else if (frames < WAIT_FRAMES) {
        frames += 1;
        frame = requestAnimationFrame(look);
      }
    };
    look();
    return () => cancelAnimationFrame(frame);
  }, [hash]);
}
